/*
 * @Description: 租户相关状态管理
 */
import { defineStore } from 'pinia';
import {
  selectTenantRequest, TenantInfo, CurrentTenantInfo, ExpandInfo,
} from '@/api/modules/tenant';
import { LocalStorageService } from '@/utils/storage';
import { actions } from '@/hooks/use-app-actions';
import { crossTabCommunication } from '@/utils/cross-tab';

/**
 * @description: 动态设置页面 favicon
 * @param logoUrl logo URL（可以是 base64 或网络地址）
 */
const setFavicon = (logoUrl?: string) => {
  if (!logoUrl) {
    return;
  }

  try {
    // 查找现有的 favicon link 元素
    let faviconLink = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    if (!faviconLink) {
      // 如果不存在则创建
      faviconLink = document.createElement('link');
      faviconLink.rel = 'icon';
      faviconLink.type = 'image/png';
      document.head.appendChild(faviconLink);
    }
    faviconLink.href = logoUrl;
  } catch (error) {
    console.error('设置 favicon 失败:', error);
  }
};

type State = {
  currentTenant: CurrentTenantInfo | null;
  tenantList: TenantInfo[];
  loading: boolean;
  // 权限编码前缀
  fmPermissionPrefix: string;
  fcPermissionPrefix: string;
};

const currentTenant = LocalStorageService.get('_current_tenant') as CurrentTenantInfo | null;

/**
 * @description: 从租户信息中解析 expandInfo
 * @param tenant 当前租户信息
 * @returns expandInfo 对象
 */
const parseExpandInfo = (tenant: CurrentTenantInfo | null): ExpandInfo | null => {
  if (!tenant?.expandInfo) {
    return null;
  }

  try {
    let expandInfo: ExpandInfo | string = tenant.expandInfo;
    if (typeof expandInfo === 'string') {
      expandInfo = JSON.parse(expandInfo) as ExpandInfo;
    }
    return expandInfo;
  } catch (error) {
    console.error('解析 expandInfo 失败:', error);
    return null;
  }
};

/**
 * @description: 从租户信息中解析权限编码前缀
 * @param tenant 当前租户信息
 * @returns 权限编码前缀对象
 */
const parsePermissionPrefix = (tenant: CurrentTenantInfo | null): { fmPrefix: string; fcPrefix: string } => {
  // 默认值（兼容旧逻辑）
  const defaultPrefix = { fmPrefix: 'FM_C4YUNJ_', fcPrefix: 'FC_C4YUNJ_' };

  if (!tenant?.expandInfo) {
    return defaultPrefix;
  }

  try {
    // expandInfo 可能是字符串或对象
    let expandInfo: ExpandInfo | string = tenant.expandInfo;
    if (typeof expandInfo === 'string') {
      expandInfo = JSON.parse(expandInfo) as ExpandInfo;
    }

    const appCode = expandInfo?.appCode;
    if (appCode) {
      return {
        fmPrefix: `FM_${appCode}_`,
        fcPrefix: `FC_${appCode}_`,
      };
    }
  } catch (error) {
    console.error('解析 expandInfo 失败:', error);
  }

  return defaultPrefix;
};

// 初始化权限编码前缀
const initialPrefix = parsePermissionPrefix(currentTenant);

export const useTenantStore = defineStore('_tenant', {
  state: (): State => ({
    currentTenant,
    tenantList: [],
    loading: false,
    fmPermissionPrefix: initialPrefix.fmPrefix,
    fcPermissionPrefix: initialPrefix.fcPrefix,
  }),

  getters: {
    // 获取当前租户ID
    currentTenantId: (state) => state.currentTenant?.tenantId || '',
    // 获取当前租户MD5加密ID
    currentTenantMid: (state) => state.currentTenant?.mid || '',
    // 获取当前租户名称
    currentTenantName: (state) => state.currentTenant?.tenantName || '',
    // 获取 FM 权限编码前缀
    fmPermission: (state) => state.fmPermissionPrefix,
    // 获取 FC 权限编码前缀
    fcPermission: (state) => state.fcPermissionPrefix,
  },

  actions: {
    /**
     * @description: 选择租户
     * @param {string} tenantId 租户ID，不传则获取当前租户和租户列表
     */
    async selectTenant(tenantId?: string) {
      this.loading = true;
      try {
        const { data } = await selectTenantRequest(tenantId);

        if (data) {
          // 更新当前租户信息
          this.currentTenant = data.currentTenant;
          this.tenantList = data.tenantList;

          // 解析并更新权限编码前缀
          const prefix = parsePermissionPrefix(data.currentTenant);
          this.fmPermissionPrefix = prefix.fmPrefix;
          this.fcPermissionPrefix = prefix.fcPrefix;

          // 同步权限编码前缀和当前租户信息到全局状态（供子应用使用）
          actions.setGlobalState({
            fmPermissionPrefix: this.fmPermissionPrefix,
            fcPermissionPrefix: this.fcPermissionPrefix,
            currentTenant: this.currentTenant,
          });

          // 保存到本地存储
          if (tenantId) {
            LocalStorageService.remove('_current_tenant');
          } else {
            LocalStorageService.set('_current_tenant', this.currentTenant);
          }

          // 更新 favicon
          const expandInfo = parseExpandInfo(this.currentTenant);
          if (expandInfo?.tenantLogo) {
            setFavicon(expandInfo.tenantLogo);
          }
        }

        return data;
      } catch (error) {
        console.error('选择租户失败:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * @description: 切换租户
     * @param {string} tenantId 目标租户ID
     */
    async switchTenant(tenantId: string) {
      if (this.currentTenant?.tenantId === tenantId) {
        console.log('已经是当前租户，无需切换');
        return;
      }

      await this.selectTenant(tenantId);

      // 获取租户配置的域名
      const expandInfo = parseExpandInfo(this.currentTenant);
      const tenantDomain = expandInfo?.tenantDomain;

      // 通知其他页签切换租户（携带目标域名）
      crossTabCommunication.notifyTenantSwitch(tenantId, tenantDomain);

      // 如果配置了域名则跳转到配置的域名，否则刷新当前页
      if (tenantDomain) {
        // 确保域名有协议前缀
        const url = tenantDomain.startsWith('http') ? tenantDomain : `https://${tenantDomain}`;
        window.location.href = url;
      } else {
        window.location.href = window.location.origin;
      }
    },

    /**
     * @description: 获取租户列表
     */
    async getTenantList() {
      if (this.tenantList.length > 0) {
        return this.tenantList;
      }

      await this.selectTenant();
      return this.tenantList;
    },

    /**
     * @description: 清空租户信息
     */
    clearTenant() {
      this.currentTenant = null;
      this.tenantList = [];
      LocalStorageService.remove('_current_tenant');
    },
  },
});
